import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { WorkspaceType } from '@/api/onboarding/onboardingApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { FieldError } from '@/components/ui/fieldError';
import { Icon } from '@/components/common/Icon';
import { useCreateAdminWorkspaceMutation } from '@/hooks/useOnboardingMutations';
import { isBlank, isValidEmail } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface CreateWorkspaceModalProps {
  onClose: () => void;
}

const LANGUAGE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'pl', labelKey: 'workspaces.languagePolish' },
  { value: 'en', labelKey: 'workspaces.languageEnglish' },
];

export function CreateWorkspaceModal({ onClose }: CreateWorkspaceModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const createMutation = useCreateAdminWorkspaceMutation();

  const [tenantType, setTenantType] = useState<WorkspaceType>('solo');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('pl');
  const [ownerFullName, setOwnerFullName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const isOrganization = tenantType === 'organization';

  const nameError = showErrors === true && isBlank(name) ? 'validation:requiredField' : null;
  const ownerEmailError =
    showErrors === true && isValidEmail(ownerEmail) === false ? 'validation:email' : null;

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isBlank(name) || isValidEmail(ownerEmail) === false) {
      setShowErrors(true);
      return;
    }
    createMutation.mutate(
      {
        tenantType,
        name: name.trim(),
        businessName:
          isOrganization === true && businessName.trim().length > 0 ? businessName.trim() : null,
        defaultLanguage,
        ownerFullName: ownerFullName.trim().length === 0 ? null : ownerFullName.trim(),
        ownerEmail: ownerEmail.trim(),
      },
      {
        onSuccess: function handleCreated() {
          showSuccessNotification(t('admin:workspaces.createSuccess'));
          onClose();
        },
        onError: function handleError(createError) {
          const backendMessage = resolveApiErrorMessage(createError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('admin:errors.generic')
          );
        },
      }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submit}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">{t('admin:workspaces.createTitle')}</h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zoomuted" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>{t('admin:workspaces.typeLabel')}</Label>
            <div className="grid grid-cols-2 gap-2.5">
              <TypeButton
                isSelected={tenantType === 'solo'}
                iconName="UserRound"
                label={t('admin:workspaces.typeSolo')}
                onSelect={function selectSolo() {
                  setTenantType('solo');
                }}
              />
              <TypeButton
                isSelected={tenantType === 'organization'}
                iconName="Building"
                label={t('admin:workspaces.typeOrganization')}
                onSelect={function selectOrganization() {
                  setTenantType('organization');
                }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="workspace-name">{t('admin:workspaces.nameLabel')}</Label>
            <Input
              id="workspace-name"
              value={name}
              hasError={nameError !== null}
              placeholder={t('admin:workspaces.namePlaceholder')}
              onChange={function updateName(changeEvent) {
                setName(changeEvent.target.value);
              }}
            />
            <FieldError messageKey={nameError} />
          </div>

          {isOrganization === true ? (
            <div className="space-y-1.5">
              <Label htmlFor="workspace-business">
                {t('admin:workspaces.businessNameLabel')}
              </Label>
              <Input
                id="workspace-business"
                value={businessName}
                onChange={function updateBusiness(changeEvent) {
                  setBusinessName(changeEvent.target.value);
                }}
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label>{t('admin:workspaces.languageLabel')}</Label>
            <div className="flex gap-2">
              {LANGUAGE_OPTIONS.map(function renderLanguage(option) {
                const isSelected = defaultLanguage === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={function selectLanguage() {
                      setDefaultLanguage(option.value);
                    }}
                    className={
                      'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                      (isSelected === true
                        ? 'bg-zooprimary text-zoowhite'
                        : 'bg-zoowhite border border-zooborder text-zoomuted')
                    }
                  >
                    {t('admin:' + option.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-2 border-t border-zooborder">
            <div className="flex items-center gap-2 text-zooindigo font-semibold text-sm mb-3 mt-3">
              <Icon name="Crown" className="text-zoowarningtext" />
              {t('admin:workspaces.ownerTitle')}
            </div>
            <div className="space-y-3.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="owner-name">{t('admin:workspaces.ownerNameLabel')}</Label>
                  <Input
                    id="owner-name"
                    value={ownerFullName}
                    onChange={function updateOwnerName(changeEvent) {
                      setOwnerFullName(changeEvent.target.value);
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="owner-email">{t('admin:workspaces.ownerEmailLabel')}</Label>
                  <Input
                    id="owner-email"
                    type="email"
                    value={ownerEmail}
                    hasError={ownerEmailError !== null}
                    placeholder={t('admin:workspaces.ownerEmailPlaceholder')}
                    onChange={function updateOwnerEmail(changeEvent) {
                      setOwnerEmail(changeEvent.target.value.trim());
                    }}
                  />
                  <FieldError messageKey={ownerEmailError} />
                </div>
              </div>
              <p className="text-[11px] text-zoomuted flex items-center gap-1.5">
                <Icon name="Send" className="text-zooviolet" />
                {t('admin:workspaces.ownerHint')}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            {t('admin:workspaces.createAction')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}

interface TypeButtonProps {
  isSelected: boolean;
  iconName: 'UserRound' | 'Building';
  label: string;
  onSelect: () => void;
}

function TypeButton({ isSelected, iconName, label, onSelect }: TypeButtonProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        'p-3 rounded-xl text-left border transition-colors ' +
        (isSelected === true
          ? 'border-zooprimary ring-2 ring-zooprimary bg-zoovpalelavender'
          : 'border-zooborder')
      }
    >
      <span className="text-sm font-bold text-zooindigo flex items-center gap-1.5">
        <Icon name={iconName} className="text-zooprimary" />
        {label}
      </span>
    </button>
  );
}
